import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { Button } from '@openng/optimus-ui/button';
import { Card } from '@openng/optimus-ui/card';
import { Divider } from '@openng/optimus-ui/divider';
import { DialogService } from '@openng/optimus-ui/dynamicdialog';
import { Tooltip } from '@openng/optimus-ui/tooltip';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CommercialContact } from 'src/app/model/commercial-services/commercial-contact';
import { CommercialFacility } from 'src/app/model/commercial-services/commercial-facility';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import {
  CommercialFacilityContactsDialogComponent,
  CommercialFacilityContactsDialogData,
} from '../commercial-facility-contacts-dialog/commercial-facility-contacts-dialog.component';

export interface RoleGroup {
  jobRoleId: string;
  name: string;
  members: { contact: CommercialContact; position: number }[];
}

@Component({
  selector: 'app-commercial-facility-contacts',
  templateUrl: './commercial-facility-contacts.component.html',
  styleUrls: ['./commercial-facility-contacts.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [Button, Card, Divider, Tooltip, TranslatePipe],
})
export class CommercialFacilityContactsComponent {
  readonly control = input<AbstractControl | null>(null);
  readonly facility = input<CommercialFacility | null>(null);
  readonly contacts = input<CommercialContact[]>([]);
  readonly isHistoricalRecord = input(false);
  readonly writeAccess = input(false);
  readonly canOpenContacts = input(false);
  readonly openContact = output<string>();

  private readonly _logAndMessage = inject(LogAndMessageService);
  private readonly _translateService = inject(TranslateService);
  private readonly _dialogService = inject(DialogService);

  private readonly contactIds = signal<string[]>([]);
  readonly contactsDirty = signal(false);

  // Facility contacts preserve history and cover users without contact-list access.
  private readonly contactsById = computed(() => {
    const byId = new Map<string, CommercialContact>();
    this.facility()?.Contacts.forEach((contact) =>
      byId.set(contact.id, contact)
    );
    if (!this.isHistoricalRecord()) {
      this.contacts().forEach((contact) => byId.set(contact.id, contact));
    }
    return byId;
  });

  readonly orderedContacts = computed(() =>
    this.contactIds()
      .map((id) => this.contactsById().get(id))
      .filter((contact): contact is CommercialContact => !!contact)
  );

  /** Roles in the order their first contact appears in the facility list. */
  readonly roleGroups = computed<RoleGroup[]>(() => {
    const byRole = new Map<string, RoleGroup>();
    this.orderedContacts().forEach((contact, index) => {
      const group = byRole.get(contact.jobRoleId) ?? {
        jobRoleId: contact.jobRoleId,
        name: contact.jobRole?.name ?? '',
        members: [],
      };
      group.members.push({ contact, position: index + 1 });
      byRole.set(contact.jobRoleId, group);
    });
    return Array.from(byRole.values());
  });

  constructor() {
    effect((onCleanup) => {
      const control = this.control();
      this.contactIds.set(control?.value ?? []);
      this.contactsDirty.set(control?.dirty ?? false);
      if (control) {
        const subscription = control.events.subscribe(() => {
          this.contactIds.set(control.value ?? []);
          this.contactsDirty.set(control.dirty);
        });
        onCleanup(() => subscription.unsubscribe());
      }
    });
  }

  fullName(contact: CommercialContact): string {
    return `${contact.firstName} ${contact.lastName}`.trim();
  }

  openManageContacts(): void {
    if (!this.getEditableControl()) {
      return;
    }
    const data: CommercialFacilityContactsDialogData = {
      assigned: this.orderedContacts(),
      available: this.contacts(),
    };
    this._dialogService
      .open(CommercialFacilityContactsDialogComponent, {
        header: this._translateService.instant(
          'COMMERCIAL_SERVICES.LABEL.MANAGE_CONTACTS'
        ),
        modal: true,
        closable: true,
        draggable: false,
        width: '56rem',
        breakpoints: { '960px': '96vw' },
        data,
      })
      ?.onClose.subscribe((ids?: string[]) => {
        if (ids) {
          this.applyContactIds(ids);
        }
      });
  }

  private applyContactIds(ids: string[]): void {
    // Re-check: access may have been lost while the dialog was open.
    const control = this.getEditableControl();
    if (!control) {
      return;
    }
    control.markAsDirty();
    control.setValue(ids);
  }

  private getEditableControl(): AbstractControl | null {
    const control = this.control();
    if (
      !control ||
      control.disabled ||
      !this.writeAccess() ||
      this.isHistoricalRecord()
    ) {
      this._logAndMessage.translateToErrorAlert({
        headerKey: 'COMMON.MESSAGES.ERROR.NO_PERMISSION_HEADER',
        bodyKey: 'COMMON.MESSAGES.ERROR.NO_PERMISSION_BODY',
        bodyParams: {
          action: this._translateService
            .instant('COMMERCIAL_SERVICES.LABEL.MANAGE_CONTACTS')
            .toLowerCase(),
        },
      });
      return null;
    }
    return control;
  }
}
